import { X } from 'lucide-react';
import { useMemo, useRef, useId, type ReactElement, type RefObject } from 'react';

import { Dialog } from '@/components/Dialog';
import { IconButton } from '@/components/IconButton';
import {
  KEYBOARD_SHORTCUT_AREA_LABEL_KEYS,
  type KeyboardShortcutDefinition,
} from '@/lib/keyboard-shortcuts';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export interface ShortcutsOverlayProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnFocusRef?: RefObject<HTMLElement | null> | undefined;
  shortcuts: readonly KeyboardShortcutDefinition[];
  singleKeyShortcutsDisabled: boolean;
  onSingleKeyShortcutsDisabledChange: (disabled: boolean) => void;
  preferencesBusy?: boolean | undefined;
  reducedMotionOverride: boolean;
  onReducedMotionOverrideChange: (enabled: boolean) => void;
}

type ShortcutGroup = readonly [
  KeyboardShortcutDefinition['area'],
  readonly KeyboardShortcutDefinition[],
];

/**
 * FE-80.1 — ⚠ THESE ARE OPAQUE IDENTIFIERS, not display English. Grouping and
 * the DOM id read the identifier; only the heading text is resolved, through
 * `KEYBOARD_SHORTCUT_AREA_LABEL_KEYS`. Translate the identifier and
 * `shortcuts-card-detail` changes with the language, which breaks every
 * `aria-labelledby` in this dialog with nothing red in English.
 */
const AREA_ORDER: readonly KeyboardShortcutDefinition['area'][] = [
  'Board',
  'Cards',
  'Card detail',
  'Navigation',
  'Activity',
  'Editing',
];

export function ShortcutsOverlay({
  open,
  onOpenChange,
  returnFocusRef,
  shortcuts,
  singleKeyShortcutsDisabled,
  onSingleKeyShortcutsDisabledChange,
  preferencesBusy = false,
  reducedMotionOverride,
  onReducedMotionOverrideChange,
}: ShortcutsOverlayProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const closeRef = useRef<HTMLButtonElement>(null);
  const groups = useMemo<ShortcutGroup[]>(() => groupShortcuts(shortcuts), [shortcuts]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('shellShortcuts.overlay.title')}
      size="lg"
      initialFocus={closeRef}
      {...(returnFocusRef === undefined ? {} : { returnFocus: returnFocusRef })}
      className="max-h-[min(92vh,48rem)]"
      closeControl={
        <IconButton ref={closeRef} label={t('shellShortcuts.overlay.close')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
    >
      <div className="space-y-5">
        <section
          aria-label={t('shellShortcuts.overlay.preferencesLabel')}
          className="grid gap-3 border-b border-border pb-4 sm:grid-cols-2"
        >
          <ToggleRow
            label={t('shellShortcuts.overlay.singleKeyLabel')}
            checked={singleKeyShortcutsDisabled}
            disabled={preferencesBusy}
            onChange={onSingleKeyShortcutsDisabledChange}
            description={t('shellShortcuts.overlay.singleKeyDescription')}
          />
          <ToggleRow
            label={t('shellShortcuts.overlay.reducedMotionLabel')}
            checked={reducedMotionOverride}
            disabled={preferencesBusy}
            onChange={onReducedMotionOverrideChange}
            description={t('shellShortcuts.overlay.reducedMotionDescription')}
          />
        </section>

        <div className="grid gap-5 sm:grid-cols-2">
          {groups.map(([area, definitions]) => (
            <section key={area} aria-labelledby={`shortcuts-${areaId(area)}`}>
              <h3
                id={`shortcuts-${areaId(area)}`}
                className="font-display text-lg leading-tight text-ink"
              >
                {t(KEYBOARD_SHORTCUT_AREA_LABEL_KEYS[area])}
              </h3>
              <ul className="mt-2 divide-y divide-border">
                {definitions.map((shortcut) => {
                  const availability =
                    shortcut.availability ??
                    (shortcut.singleKey && singleKeyShortcutsDisabled
                      ? 'disabled'
                      : undefined);
                  return (
                    <li
                      key={shortcut.id}
                      className={cn(
                        'grid grid-cols-[minmax(0,1fr)_auto] gap-3 py-2 text-sm',
                        availability === 'unavailable' && 'opacity-60',
                      )}
                    >
                      <span className="min-w-0 text-ink">
                        {shortcut.label}
                        <ShortcutStatus t={t} availability={availability} />
                      </span>
                      <ShortcutKeys
                        t={t}
                        keys={shortcut.keys}
                        disabled={availability === 'disabled'}
                      />
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </Dialog>
  );
}

function groupShortcuts(
  shortcuts: readonly KeyboardShortcutDefinition[],
): ShortcutGroup[] {
  const byArea = new Map<
    KeyboardShortcutDefinition['area'],
    KeyboardShortcutDefinition[]
  >();
  for (const area of AREA_ORDER) byArea.set(area, []);
  for (const shortcut of shortcuts) {
    byArea.get(shortcut.area)?.push(shortcut);
  }

  return AREA_ORDER.flatMap((area) => {
    const definitions = byArea.get(area) ?? [];
    return definitions.length === 0 ? [] : [[area, definitions] as const];
  });
}

function ShortcutStatus({
  t,
  availability,
}: {
  t: InterfaceTranslator;
  availability?: KeyboardShortcutDefinition['availability'] | undefined;
}) {
  if (availability === 'unavailable') {
    return (
      <span className="ml-2 align-middle text-xs font-medium text-ink-muted">
        {t('shellShortcuts.overlay.statusUnavailable')}
      </span>
    );
  }
  if (availability === 'disabled') {
    return (
      <span className="ml-2 align-middle text-xs font-medium text-ink-muted">
        {t('shellShortcuts.overlay.statusDisabled')}
      </span>
    );
  }
  return null;
}

function ToggleRow({
  label,
  checked,
  disabled,
  onChange,
  description,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
  description: string;
}) {
  const id = useId();
  return (
    <div className="flex min-w-0 items-start gap-3 rounded-card border border-border bg-surface-raised px-3 py-2">
      <input
        id={id}
        type="checkbox"
        className="mt-1 size-4 shrink-0 accent-accent"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <span className="min-w-0">
        <label htmlFor={id} className="block text-sm font-medium text-ink">
          {label}
        </label>
        <span className="block text-xs text-ink-muted">{description}</span>
      </span>
    </div>
  );
}

function keysAccessibleName(t: InterfaceTranslator, keys: readonly string[]): string {
  return keys.reduceRight(
    (rest, key) =>
      rest === ''
        ? key
        : t('shellShortcuts.overlay.keysPair', { first: key, second: rest }),
    '',
  );
}

function ShortcutKeys({
  t,
  keys,
  disabled,
}: {
  t: InterfaceTranslator;
  keys: readonly string[];
  disabled: boolean;
}) {
  return (
    <span
      aria-label={keysAccessibleName(t, keys)}
      className={cn(
        'flex max-w-[11rem] flex-wrap justify-end gap-1',
        disabled && 'text-ink-muted',
      )}
    >
      {keys.map((key) => (
        <kbd
          key={key}
          aria-hidden="true"
          className="rounded-sm border border-border-strong bg-surface px-1.5 py-0.5 font-mono text-[0.75rem] leading-tight"
        >
          {key}
        </kbd>
      ))}
    </span>
  );
}

/** Reads the OPAQUE area identifier, never the resolved heading. See AREA_ORDER. */
function areaId(area: KeyboardShortcutDefinition['area']): string {
  return area.toLowerCase().replaceAll(' ', '-');
}
